
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Calendar, Home, Users, Shield, Clock, Car, Wifi, Utensils, Tv, Wind, Droplets, Zap, Building, Phone, Mail, User, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ListingDetailInfoProps {
  listing: {
    title: string;
    address: string;
    distance_km: number;
    price_egp: number;
    deposit_egp: number;
    unit_type: string;
    gender_rules: string;
    furnished: boolean;
    verified: boolean;
    available_from: string;
    lease_min_months: number;
    roommates_allowed?: boolean;
    amenities?: string[] | null;
    video_url?: string;
    floor_plan_url?: string;
    whatsapp_link?: string;
    google_maps_link?: string;
    rental_status?: string;
    approval_status?: string;
    universities?: {
      name_en: string;
      name_ar: string;
      city: string;
    } | null;
    profiles?: {
      full_name: string;
      phone: string;
      email: string;
    } | null;
  };
}

const ListingDetailInfo = ({ listing }: ListingDetailInfoProps) => {
  const { language, t } = useLanguage();

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      apartment: { en: 'Apartment', ar: 'شقة' },
      room: { en: 'Room', ar: 'غرفة' },
      studio: { en: 'Studio', ar: 'استوديو' },
      house: { en: 'House', ar: 'منزل' },
      villa: { en: 'Villa', ar: 'فيلا' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      any: { en: 'Any Gender', ar: 'أي جنس' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' },
      mixed: { en: 'Mixed Gender', ar: 'مختلط' }
    };
    return labels[gender as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || gender;
  };

  const getAmenityIcon = (amenity: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      wifi: <Wifi className="h-4 w-4" />,
      parking: <Car className="h-4 w-4" />,
      kitchen: <Utensils className="h-4 w-4" />,
      tv: <Tv className="h-4 w-4" />,
      ac: <Wind className="h-4 w-4" />,
      water: <Droplets className="h-4 w-4" />,
      electricity: <Zap className="h-4 w-4" />,
      elevator: <Building className="h-4 w-4" />,
      security: <Shield className="h-4 w-4" />
    };
    return iconMap[amenity.toLowerCase()] || <Home className="h-4 w-4" />;
  };

  const getAmenityLabel = (amenity: string) => {
    const labels: { [key: string]: { en: string; ar: string } } = {
      wifi: { en: 'WiFi', ar: 'واي فاي' },
      parking: { en: 'Parking', ar: 'موقف سيارات' },
      kitchen: { en: 'Kitchen', ar: 'مطبخ' },
      tv: { en: 'TV', ar: 'تلفزيون' },
      ac: { en: 'Air Conditioning', ar: 'تكييف' },
      water: { en: 'Water', ar: 'مياه' },
      electricity: { en: 'Electricity', ar: 'كهرباء' },
      elevator: { en: 'Elevator', ar: 'مصعد' },
      security: { en: '24/7 Security', ar: 'أمن 24/7' },
      furnished: { en: 'Furnished', ar: 'مفروش' },
      gym: { en: 'Gym', ar: 'نادي رياضي' },
      pool: { en: 'Swimming Pool', ar: 'حمام سباحة' },
      laundry: { en: 'Laundry', ar: 'غسيل ملابس' }
    };
    return labels[amenity.toLowerCase()]?.[language === 'ar' ? 'ar' : 'en'] || amenity;
  };

  return (
    <div className="space-y-6">
      {/* Title and Location */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
          {listing.title}
        </h1>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{listing.address}</span>
          </div>
          {listing.universities && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">
                {listing.distance_km}km from {' '}
                {language === 'ar' ? listing.universities?.name_ar : listing.universities?.name_en}
                {listing.universities?.city && ` - ${listing.universities.city}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Price Section */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="text-3xl font-bold text-primary">
            {listing.price_egp.toLocaleString()} {t('egp', 'ج.م', 'EGP')}
            <span className="text-base font-normal text-muted-foreground ml-2">
              /{t('month', 'شهر', 'month')}
            </span>
          </div>
          {listing.deposit_egp > 0 && (
            <div className="text-sm text-muted-foreground mt-1">
              + {listing.deposit_egp.toLocaleString()} {t('egp', 'ج.م', 'EGP')} {t('deposit', 'تأمين', 'Deposit')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Property Tags */}
      <div className="flex flex-wrap gap-2">
        {listing.verified && (
          <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300">
            <Shield className="h-3 w-3 mr-1" />
            {t('verified', 'موثق', 'Verified')}
          </Badge>
        )}
        <Badge variant="outline" className="text-sm">
          {getUnitTypeLabel(listing.unit_type)}
        </Badge>
        <Badge variant="outline" className="text-sm">
          {getGenderLabel(listing.gender_rules)}
        </Badge>
        {listing.furnished && (
          <Badge variant="outline" className="text-sm">
            <Home className="h-3 w-3 mr-1" />
            {t('furnished', 'مفروش', 'Furnished')}
          </Badge>
        )}
        {listing.roommates_allowed && (
          <Badge variant="outline" className="text-sm">
            <Users className="h-3 w-3 mr-1" />
            {t('roommates_allowed', 'مسموح شركاء السكن', 'Roommates Allowed')}
          </Badge>
        )}
        {listing.rental_status === 'available' && (
          <Badge className="bg-green-600 hover:bg-green-700">
            {t('available_now', 'متاح الآن', 'Available Now')}
          </Badge>
        )}
      </div>

      {/* Key Information Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('key_information', 'المعلومات الأساسية', 'Key Information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
              <div>
                <div className="font-medium text-sm">{t('available_from', 'متاح من', 'Available From')}</div>
                <div className="text-muted-foreground text-sm">
                  {new Date(listing.available_from).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Clock className="h-5 w-5 text-primary flex-shrink-0" />
              <div>
                <div className="font-medium text-sm">{t('minimum_lease', 'أقل مدة إيجار', 'Minimum Lease')}</div>
                <div className="text-muted-foreground text-sm">
                  {listing.lease_min_months} {listing.lease_min_months === 1 ? t('month', 'شهر', 'month') : t('months', 'شهور', 'months')}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Amenities */}
      {listing.amenities && listing.amenities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('amenities_features', 'المرافق والخصائص', 'Amenities & Features')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {listing.amenities.map((amenity, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  {getAmenityIcon(amenity)}
                  <span className="text-sm font-medium">{getAmenityLabel(amenity)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Resources */}
      {(listing.video_url || listing.floor_plan_url) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('additional_resources', 'موارد إضافية', 'Additional Resources')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {listing.video_url && (
              <a 
                href={listing.video_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Tv className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('property_video', 'فيديو العقار', 'Property Video')}</span>
                <ExternalLink className="h-4 w-4 ml-auto" />
              </a>
            )}
            
            {listing.floor_plan_url && (
              <a 
                href={listing.floor_plan_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Home className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('floor_plan', 'مخطط الطابق', 'Floor Plan')}</span>
                <ExternalLink className="h-4 w-4 ml-auto" />
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {/* Owner Information (if available) */}
      {listing.profiles && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('property_owner', 'مالك العقار', 'Property Owner')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-primary" />
              <div>
                <div className="font-medium">{listing.profiles.full_name}</div>
                <div className="text-sm text-muted-foreground">
                  {t('property_owner', 'مالك العقار', 'Property Owner')}
                </div>
              </div>
            </div>
            
            {listing.profiles.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{listing.profiles.phone}</span>
              </div>
            )}
            
            {listing.profiles.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{listing.profiles.email}</span>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground mt-2">
              {t('response_rate_info', '95% معدل الاستجابة', '95% response rate')}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ListingDetailInfo;
